import { useEffect, useState } from 'react';
import { Check, Plus, Undo2 } from 'lucide-react';
import { Modal } from '../../../components/ui/Modal';
import {
  assignVehicleToCompany,
  listCustomCompanies,
  returnVehicleToBase,
} from '../../../db/fleet-store';
import { listCompanyOptions, rememberableCompany } from '../../../core/company-directory';
import type { CompanyOption } from '../../../core/company-directory';
import {
  describeVehicleLocation,
  isHomeCompany,
  normalizeCompany,
} from '../../../core/vehicle-location';
import { vehicleCallSign } from '../../../core/military-id';
import type { Vehicle } from '../../../types/fleet';
import type { User } from '../../../types/user';

interface VehicleTransferModalProps {
  user: User;
  vehicle: Vehicle;
  /**
   * The pool, so a company already holding one of our vehicles is offered even
   * if this device never typed it in — a restored backup brings its own.
   */
  vehicles?: readonly Vehicle[];
  onClose: () => void;
  onSaved: () => Promise<void> | void;
}

/**
 * "השאלה לפלוגה" — hand one vehicle to another company, or bring it back.
 *
 * Deliberately one screen with one decision on it, and the decision is a single
 * tap: every company the unit lends to is a radio row, שינוע is the way back,
 * and a company that is not on the list yet is typed into "+ הוסף פלוגה חדשה"
 * and remembered for next time. Picking a row writes immediately — this sheet
 * is what the roster badge opens, and a dispatcher moving eight vehicles before
 * a convoy should not press save eight times.
 *
 * Which companies are offered is decided in `src/core/company-directory.ts` and
 * what the move does to the vehicle's status in `src/core/vehicle-location.ts`,
 * never here — the sheet only says where the vehicle is going.
 */
export function VehicleTransferModal({
  user,
  vehicle,
  vehicles = [],
  onClose,
  onSaved,
}: VehicleTransferModalProps) {
  const location = describeVehicleLocation(vehicle);
  /** Companies the unit typed in on an earlier transfer. */
  const [remembered, setRemembered] = useState<readonly string[]>([]);
  /** The "+ הוסף פלוגה חדשה" field, once it has been opened. */
  const [custom, setCustom] = useState<string | null>(null);
  /** The company being written, so the row that was tapped is the one that waits. */
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    void listCustomCompanies().then((names) => {
      if (live) setRemembered(names);
    });
    return () => {
      live = false;
    };
  }, []);

  const options = listCompanyOptions(vehicles.length > 0 ? vehicles : [vehicle], remembered);
  const holding = (option: CompanyOption): boolean =>
    option.home ? location.kind !== 'loaned' : normalizeCompany(option.name) === location.company;

  const commit = async (company: string) => {
    setSaving(company);
    setError(null);
    try {
      if (isHomeCompany(company)) await returnVehicleToBase(user, vehicle.id);
      else await assignVehicleToCompany(user, vehicle.id, company);
      await onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setSaving(null);
    }
  };

  const typed = rememberableCompany(custom);

  const rowClass = (current: boolean) =>
    [
      'w-full h-12 px-3 rounded-xl border text-sm font-medium transition-colors',
      'flex items-center gap-2 disabled:opacity-40',
      current
        ? 'border-brand-500 bg-brand-50 text-brand-700'
        : 'border-line bg-panel text-ink/70 hover:text-ink',
    ].join(' ');

  return (
    <Modal
      title="שיוך הרכב לפלוגה"
      subtitle={`${vehicle.name || vehicle.model} · ${vehicleCallSign(vehicle)} · ${location.label}`}
      onClose={onClose}
      footer={
        <button
          type="button"
          onClick={onClose}
          className="h-10 px-4 rounded-xl border border-line text-sm text-ink/70 hover:text-ink"
        >
          סגירה
        </button>
      }
    >
      <div className="space-y-4">
        <div>
          <p className="text-xs text-ink/60 mb-2">בחרו את הפלוגה שתחזיק ברכב — הבחירה נשמרת מיד</p>
          <div role="radiogroup" aria-label="בחירת פלוגה" className="grid gap-2 sm:grid-cols-2">
            {options.map((option) => {
              const current = holding(option);
              return (
                <button
                  key={option.name}
                  type="button"
                  role="radio"
                  aria-checked={current}
                  data-company={option.name}
                  disabled={saving !== null}
                  onClick={() => void commit(option.name)}
                  className={rowClass(current)}
                >
                  {option.home ? (
                    <Undo2 size={16} aria-hidden className="shrink-0" />
                  ) : (
                    <span
                      aria-hidden
                      className={[
                        'shrink-0 w-4 h-4 rounded-full border',
                        current ? 'border-brand-500 bg-brand-500' : 'border-line',
                      ].join(' ')}
                    />
                  )}
                  <span className="truncate">
                    {saving === option.name
                      ? 'מעביר…'
                      : option.home
                        ? 'החזר לשינוע'
                        : option.name}
                  </span>
                  {option.custom ? (
                    <span className="ms-auto shrink-0 text-[10px] text-ink/40">נוספה ידנית</span>
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>

        {custom === null ? (
          <button
            type="button"
            onClick={() => setCustom('')}
            className="w-full h-11 rounded-xl border border-dashed border-line text-sm text-ink/70 hover:text-ink flex items-center justify-center gap-2"
          >
            <Plus size={16} aria-hidden />
            הוסף פלוגה חדשה
          </button>
        ) : (
          <div className="rounded-xl border border-line p-3 space-y-2">
            <label className="block">
              <span className="block text-xs text-ink/60 mb-1">שם הפלוגה החדשה</span>
              <input
                value={custom}
                autoFocus
                onChange={(event) => setCustom(event.target.value)}
                placeholder="למשל: פלוגת הנדסה"
                className="w-full h-10 rounded-xl border border-line bg-canvas px-3 text-sm focus:outline-none focus:border-brand-500 focus:bg-panel"
              />
            </label>
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setCustom(null)}
                className="h-10 px-4 rounded-xl border border-line text-sm text-ink/70 hover:text-ink"
              >
                ביטול
              </button>
              <button
                type="button"
                disabled={saving !== null || !typed}
                onClick={() => void commit(typed ?? '')}
                className="h-10 px-5 rounded-xl bg-brand-500 text-white text-sm font-medium hover:bg-brand-700 disabled:opacity-40 flex items-center gap-2"
              >
                <Check size={16} aria-hidden />
                הוסף והעבר
              </button>
            </div>
          </div>
        )}

        <p className="text-xs text-ink/50">
          העברה מעדכנת את מיקום הרכב ואת הסטטוס שלו ונרשמת בהיסטוריית ההשאלות. רכב המסומן
          "בטיפול / תקול" נשאר מסומן כך גם לאחר ההעברה — מעבר בין פלוגות אינו תיקון.
        </p>

        {error ? (
          <p className="text-sm rounded-xl border border-rose-200 bg-rose-50 text-rose-700 p-3">
            {error}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}
